import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { CheckCircle, HourglassLow, Warning } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import React, { useEffect } from 'react';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';

import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import RegistrationStatusMessage from '@/components/RegistrationStatus/RegistrationStatusMessage';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { EventContext } from '@/redux/modules/analytics';
import {
    getCatalogCurrency,
    getCatalogIsCatalogOnly,
    getCatalogIsInstantAutopay,
    getCatalogIsTimedPlusBiddingOpen,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { fetchCatalogRegistrationsForUserByIdIfNeeded } from '@/redux/modules/catalog/registration/catalogRegistration.actions';
import {
    getIsRegistered,
    getRegistrationApproved,
    getRegistrationApprovedWithLimit,
    getRegistrationBidLimit,
    getRegistrationDeclined,
    getRegistrationPending,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { CompleteAccountModalContext, ModalContext, openRegisterForAuctionModal } from '@/redux/modules/modal';
import { useTrackRegisterForAuctionPressedAnalytics } from '@/utils/amplitude/analytics/registration/trackRegisterForAuctionPressed.analytics';

type Props = {
    catalogId: number;
    itemId?: number;
    justify?: boolean;
    showRegister?: boolean;
};

const RegistrationStatusLabel = ({ catalogId, itemId, justify, showRegister = true }: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const approved = useAppSelector((state) => getRegistrationApproved(state, catalogId));
    const limited = useAppSelector((state) => getRegistrationApprovedWithLimit(state, catalogId));
    const pending = useAppSelector((state) => getRegistrationPending(state, catalogId));
    const bidLimit = useAppSelector((state) => getRegistrationBidLimit(state, catalogId));
    const currency = useAppSelector((state) => getCatalogCurrency(state, catalogId));
    const registered = useAppSelector((state) => getIsRegistered(state, catalogId));
    const declined = useAppSelector((state) => getRegistrationDeclined(state, catalogId));
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));
    const timedPlusBiddingOpen = useAppSelector((state) => getCatalogIsTimedPlusBiddingOpen(state, catalogId));
    const trackRegisterForAuctionPressedAnalytics = useTrackRegisterForAuctionPressedAnalytics();

    const modalContext = React.useMemo<ModalContext>(
        () => ({
            catalogId,
            eventContext: EventContext.registerForAuction,
            id: catalogId,
            type: CompleteAccountModalContext.RegisterForAuction,
        }),
        [catalogId]
    );

    const registerForCatalog = () => {
        trackRegisterForAuctionPressedAnalytics();
        dispatch(
            openRegisterForAuctionModal({
                catalogId,
                fromBiddingConsole: false,
                isInstantAutopay,
                itemId,
                modalContext,
            })
        );
    };

    useEffect(() => {
        dispatch(
            fetchCatalogRegistrationsForUserByIdIfNeeded({
                catalogIds: [catalogId],
            })
        );
    }, [catalogId, dispatch]);

    let label = null;

    const needsToRegister = !registered;

    if (needsToRegister) {
        label = showRegister ? (
            <StyledRegisterButton
                data-testid="catalogRegisterFromBannerButton"
                onClick={registerForCatalog}
                style={timedPlusBiddingOpen ? 'critical' : 'primary'}
                variant="filled"
            >
                {formatMessage({
                    id: isCatalogOnly
                        ? 'registration_status_label.request_online_bidding'
                        : timedPlusBiddingOpen
                          ? 'registration_status_label.register_to_bid'
                          : 'registration_status_label.register_for_auction',
                })}
            </StyledRegisterButton>
        ) : null;
    } else if (isCatalogOnly && registered) {
        label = (
            <StyledLabel>
                <RegistrationStatusMessage
                    alertType="approved"
                    header={<FormattedMessage id="registration_status_label.request_submitted" />}
                    icon={CheckCircle}
                    justify={justify}
                    noMargin
                />
            </StyledLabel>
        );
    } else if (pending) {
        label = (
            <StyledLabel>
                <RegistrationStatusMessage
                    alertType="pending"
                    header={<FormattedMessage id="registration_status_label.registration_pending" />}
                    icon={HourglassLow}
                    justify={justify}
                    noMargin
                />
            </StyledLabel>
        );
    } else if (limited) {
        label = (
            <StyledLabel>
                <RegistrationStatusMessage
                    alertType="approved"
                    header={
                        <FormattedMessage
                            id="bid_limit_approval"
                            values={{
                                limit: (
                                    <FormattedCurrency
                                        key="bid-limit-approval-limit"
                                        noFraction
                                        sourceCurrency={currency}
                                        value={bidLimit}
                                    />
                                ),
                            }}
                        />
                    }
                    icon={CheckCircle}
                    justify={justify}
                    noMargin
                />
            </StyledLabel>
        );
    } else if (approved) {
        label = (
            <StyledLabel>
                <RegistrationStatusMessage
                    alertType="approved"
                    header={<FormattedMessage id="registration_status_label.registration_approved" />}
                    icon={CheckCircle}
                    justify={justify}
                    noMargin
                />
            </StyledLabel>
        );
    } else if (declined) {
        label = (
            <StyledLabel>
                <RegistrationStatusMessage
                    alertType="declined"
                    header={<FormattedMessage id="registration_status_label.registration_declined" />}
                    icon={Warning}
                    justify={justify}
                    noMargin
                />
            </StyledLabel>
        );
    }

    return label;
};

export default React.memo<Props>(RegistrationStatusLabel, isEqual);

const StyledLabel = styled.span`
    font-size: 14px;
    line-height: 24px;
`;

const StyledRegisterButton = styled(Button)`
    width: 242px;
`;
